<template>
    <div :key="iteration">
        <div v-show="isLoading" class="widget-overlay">
            <div class="loader">
                Loading...
            </div>
        </div>

        <progress-bar v-if="hasStarted() && ! hasFinished()" :current="currentQuestion" :total="numberOfQuestions"></progress-bar>

        <section v-if=" ! hasStarted() && ! hasFinished()" class="question" id="question-0">
            <form @submit.prevent="start()">
                <div class="form-group">
                    <label for="adviser">
                        Adviser
                    </label>

                    <input
                        id="adviser"
                        type="text"
                        inputmode="text"
                        class="form-control"
                        name="adviser"
                        required
                        autocomplete="adviser"
                        v-model="adviserName"
                    >
                </div>

                <template v-if="shouldFacilitateInvestor">
                    <div v-if="investor" class="form-group">
                        <input
                            id="investor"
                            type="hidden"
                            name="investor"
                            v-model="investor"
                        >

                        <label for="investor">
                            Investor
                        </label>

                        <input
                            id="investorName"
                            type="text"
                            inputmode="text"
                            class="form-control"
                            name="investorName"
                            required
                            disabled
                            v-model="investorDisplay"
                        >

                        <button type="button" class="button" @click.stop="deselectInvestor($event)">
                            Clear Investor
                        </button>
                    </div>

                    <div v-else class="form-group">
                        <label for="investor">
                            Investor
                        </label>

                        <p>
                            <a href="/my-investors" class="button">
                                Select Investor
                            </a>
                        </p>
                    </div>
                </template>

                <div class="form-group">
                    <label for="name">
                        Name on report
                    </label>

                    <input
                        id="name"
                        type="text"
                        inputmode="text"
                        class="form-control"
                        name="name"
                        required
                        autocomplete="name"
                        v-model="clientName"
                    >
                </div>

                <div v-if="partial" class="form-group" v-html="partial"></div>

                <div class="form-group">
                    <button type="submit" class="button button-primary">
                        Start
                    </button>
                </div>
            </form>
        </section>

        <section v-if="hasStarted() && ! hasFinished()" class="question" :id="'question-' + currentQuestionId">
            <p v-if="currentQuestionQuestion">
                {{ currentQuestionQuestion }}
            </p>

            <form @submit.prevent="next()">
                <data-table v-if="currentQuestionTable"
                    ref="table"
                    :title="retrieveTitle(currentQuestionTable)"
                    :columns="currentQuestionTable.columns"
                    :colspan="currentQuestionTable.columns.headings.length"
                    :rows="currentQuestionTable.rows"
                    :limit="currentQuestionTable.limit_rows"
                    :tables="currentQuestionTable.tables"
                    :footer="currentQuestionTable.footer"
                    :textAreas="retrieveTextAreas(currentQuestionTable)"
                    :classes="retrieveClasses(currentQuestionTable)"
                    level="1"
                    heading="1"
                    :currentQuestionId="currentQuestionId"
                    :currentAnswer="currentAnswer"
                />

                <div v-else class="form-group form-group-toggle">
                    <div v-for="(option, optionIndex) in currentQuestionOptions" class="radio-group">
                        <input
                            :id="option.option"
                            type="radio"
                            class="form-control toggle"
                            :name="currentQuestionId"
                            :value="option.option"
                            required
                            :autofocus="optionIndex === 0"
                            v-model="currentAnswer"
                        >

                        <label :for="option.option" class="form-label-radio">
                            <div>{{ option.value }}</div>
                        </label>

                        <div v-if="option.warning" v-show="currentAnswer === option.option" :id="'option-warning-' + option.option" class="alert alert-warning">
                            {{ option.warning }}
                        </div>
                    </div>
                </div>

                <p v-if="currentQuestionDisclaimer">
                    {{ currentQuestionDisclaimer }}
                </p>

                <div class="form-group">
                    <button type="submit" class="button button-primary">
                        <span v-if="currentQuestion !== numberOfQuestions">Next</span>
                        <span v-else>Finish</span>
                    </button>

                    <button v-if="currentQuestion !== 1" type="button" class="button" @click.stop="previous()">
                        Previous
                    </button>
                </div>
            </form>
        </section>

        <section v-if="hasFinished()">
            <h2 v-html="summary.title"></h2>
            <p v-html="summary.description"></p>

            <p v-if="allowReport" class="center">
                <button class="button button-large" @click.stop="requestReport($event)">
                    <span>Generate Report</span>
                    <i class="fal fa-file-chart-line" aria-hidden="true"></i>
                </button>
            </p>

            <h3>Ability</h3>
            <p v-html="summary.ability.text"></p>

            <section class="page-component component-accordion">
                <div class="accordion-container">
                    <button class="accordion-title" aria-expanded="false" aria-controls="accordion-ability" @click.stop="toggleAccordion($event)">
                        <span v-html="summary.ability.feedback.title"></span>
                        <i class="fal fa-plus" aria-hidden="true"></i>
                    </button>

                    <div class="accordion-text" id="accordion-ability" hidden>
                        <template v-for="feedback in summary.ability.feedback.body">
                            <h4 v-html="feedback.heading"></h4>
                            <p v-html="feedback.text"></p>
                        </template>
                    </div>
                </div>
            </section>

            <h3>Willingness</h3>
            <p v-html="summary.willingness.text"></p>

            <section class="page-component component-accordion">
                <div class="accordion-container">
                    <button class="accordion-title" aria-expanded="false" aria-controls="accordion-willingness" @click.stop="toggleAccordion($event)">
                        <span v-html="summary.willingness.feedback.title"></span>
                        <i class="fal fa-plus" aria-hidden="true"></i>
                    </button>

                    <div class="accordion-text" id="accordion-willingness" hidden>
                        <template v-for="feedback in summary.willingness.feedback.body">
                            <h4 v-html="feedback.heading"></h4>
                            <p v-html="feedback.text"></p>
                        </template>
                    </div>
                </div>
            </section>

            <h3>Overall Risk</h3>
            <p v-html="summary.overall.text"></p>

            <section class="page-component component-accordion">
                <div class="accordion-container">
                    <button class="accordion-title" aria-expanded="false" aria-controls="accordion-overall" @click.stop="toggleAccordion($event)">
                        <span v-html="summary.overall.feedback.title"></span>
                        <i class="fal fa-plus" aria-hidden="true"></i>
                    </button>

                    <div class="accordion-text" id="accordion-overall" hidden>
                        <template v-for="feedback in summary.overall.feedback.body">
                            <h4 v-html="feedback.heading"></h4>
                            <p v-html="feedback.text"></p>
                        </template>
                    </div>
                </div>
            </section>

            <h3>Self-Assessed</h3>
            <p v-html="summary.self.text"></p>

            <section class="page-component component-accordion">
                <div class="accordion-container">
                    <button class="accordion-title" aria-expanded="false" aria-controls="accordion-self" @click.stop="toggleAccordion($event)">
                        <span v-html="summary.self.feedback.title"></span>
                        <i class="fal fa-plus" aria-hidden="true"></i>
                    </button>

                    <div class="accordion-text" id="accordion-self" hidden>
                        <template v-for="feedback in summary.self.feedback.body">
                            <h4 v-html="feedback.heading"></h4>
                            <p v-html="feedback.text"></p>
                        </template>
                    </div>
                </div>
            </section>

            <p v-if="allowReport" class="center">
                <button class="button button-large" @click.stop="requestReport($event)">
                    <span>Generate Report</span>
                    <i class="fal fa-file-chart-line" aria-hidden="true"></i>
                </button>
            </p>
        </section>
    </div>
</template>

<script>

    import Widget from './Widget';
    import MargettsApi from '../modules/margetts-api';

    export default Widget.extend({

        data() {
            return {
                iteration: 0,
                isLoading: false,
                adviserName: null,
                shouldFacilitateInvestor: false,
                investor: null,
                investorCode: null,
                investorName: null,
                investorDisplay: null,
                clientName: null,
                survey: 0,
                numberOfQuestions: 0,
                currentQuestion: 0,
                currentQuestionId: 0,
                currentQuestionQuestion: null,
                currentQuestionDisclaimer: null,
                currentQuestionOptions: [],
                currentQuestionTable: null,
                currentAnswer: null,
                isCompleted: false,
                summary: null,
                allowReport: false,
                partial: '',
                classes: 'risk-profiler-widget',
            }
        },

        created() {
            this.render();

            window.events.$on('updateInvestor', value => {
                this.investor = value;
            });

            window.events.$on('updateInvestorName', value => {
                this.clientName = value;
            });

            window.events.$on('updateCurrentAnswer', value => {
                this.currentAnswer = value;
            });
        },

        methods: {

            render() {
                this.iteration += 1;
                this.adviserName = this.parameters.adviserName;
                this.shouldFacilitateInvestor = this.parameters.shouldFacilitateInvestor;
                this.investor = this.parameters.investor;
                this.investorCode = this.parameters.investorCode;
                this.investorName = this.parameters.investorName;
                this.investorDisplay = this.investorName + ' (' + this.investorCode + ')';
                this.clientName = this.parameters.investorName;
                this.partial = this.parameters.partial;

                this.unblock();
            },

            start() {
                this.block();

                const parameters = {
                    'adviser': this.adviserName,
                    'investor': this.investor,
                    'name': this.clientName,
                    'user': 'userId'
                };

                MargettsApi.post('risk-profiler/start', this.createQueryStringFromParameters(parameters, false), response => {
                    if ( ! response || ! response.result) {
                        return MargettsApi.throwNoResponseError();
                    }

                    if (response.result !== 'success') {
                        return this.throwApiError(response.messages);
                    }

                    const data = response.data;

                    this.survey = data.survey;
                    this.numberOfQuestions = data.question_count;

                    this.setQuestion(data.next_question).then(() => {
                        this.setFocus();
                    });

                    this.unblock();

                    axios.post('/actions/startSurvey', {
                        'surveyId': this.survey
                    });
                }).catch(error => {
                    MargettsApi.throwError(error);
                });

            },

            hasStarted() {
                return this.survey && this.currentQuestion > 0;
            },

            hasFinished() {
                return this.isCompleted && this.summary;
            },

            async setQuestion(question) {
                this.currentAnswer = question.value ? question.value : null;
                this.currentQuestion = question.number;
                this.currentQuestionId = question.question;
                this.currentQuestionQuestion = question.title;
                this.currentQuestionDisclaimer = question.sub_title;
                this.currentQuestionOptions = question.options;
                this.currentQuestionTable = question.table ? question.table.data : null;
            },

            setFocus() {
                const competingElements = document.querySelectorAll('[autofocus]');

                if (competingElements.length) {
                    competingElements[0].focus();
                }
            },

            proceed(direction) {
                this.block();

                const parameters = {
                    'survey': this.survey.toString(),
                    'question': this.currentQuestionId.toString(),
                    'direction': direction
                };

                if (direction === 'next') {
                    parameters.option = this.currentAnswer;
                }

                MargettsApi.post('risk-profiler/response', this.createQueryStringFromParameters(parameters, false), response => {
                    if ( ! response || ! response.result) {
                        return MargettsApi.throwNoResponseError();
                    }

                    if (response.result !== 'success') {
                        return this.throwApiError(response.messages);
                    }

                    const data = response.data;

                    this.setQuestion(data.next_question).then(() => {
                        this.setFocus();
                    })

                    this.unblock();
                }).catch(error => {
                    MargettsApi.throwError(error);
                });
            },

            finish() {
                this.block();

                const parameters = {
                    'survey': this.survey.toString(),
                    'question': this.currentQuestionId.toString(),
                    'option': this.currentAnswer
                };

                MargettsApi.post('risk-profiler/submit', this.createQueryStringFromParameters(parameters, false), response => {
                    if ( ! response || ! response.result) {
                        return MargettsApi.throwNoResponseError();
                    }

                    if (response.result !== 'success') {
                        return this.throwApiError(response.messages);
                    }

                    const data = response.data;

                    this.isCompleted = true;
                    this.summary = data.summary;
                    this.allowReport = data.report;

                    this.unblock();
                }).catch(error => {
                    MargettsApi.throwError(error);
                });
            },

            next() {
                if (this.currentQuestion === this.numberOfQuestions) {
                    this.finish();
                } else {
                    this.proceed('next');
                }
            },

            previous() {
                this.proceed('previous');
            },

            toggleAccordion(event) {
                let item = event.currentTarget;
                let icon = item.children[1];
                let accordionText = item.nextElementSibling;

                const expanded = item.getAttribute('aria-expanded') === 'true' || false;

                item.setAttribute('aria-expanded', ! expanded);
                accordionText.hidden = expanded;

                if (expanded) {
                    icon.classList.remove('fa-minus');
                    icon.classList.add('fa-plus');
                } else {
                    icon.classList.remove('fa-plus');
                    icon.classList.add('fa-minus');
                }
            },

            requestReport(event) {
                if ( ! this.allowReport) {
                    return;
                }

                this.block();

                const parameters = {
                    'survey': this.survey.toString()
                };

                MargettsApi.post('risk-profiler/report', this.createQueryStringFromParameters(parameters, false), response => {
                    if ( ! response || ! response.result) {
                        return MargettsApi.throwNoResponseError();
                    }

                    if (response.result !== 'success') {
                        return this.throwApiError(response.messages);
                    }

                    const data = response.data;

                    if (data.file) {
                        this.awaitFileInBackground(data.file.name, data.file.path);
                    }

                    if (response.messages) {
                        flash(response.messages.join('<br>'), 'success');
                    }

                    this.unblock();
                }).catch(error => {
                    MargettsApi.throwError(error);
                });
            }
        }
    });

</script>
